import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-toastify';

import {
  initialImageToVideoState,
  routeParamsHydratedAtom,
  selectedEstateIdAtom,
  selectedImagesAtom,
  selectedObjectAtom,
  selectedProjectIdAtom,
  type ImageRef,
} from '@/features/image-to-video/atoms/state';
import { useImageToVideoActions } from '@/features/image-to-video/hooks/useImageToVideoActions';
import { useSetImageToVideoState } from '@/features/image-to-video/atoms/selectors';
import { MainSidebar } from '@/features/image-to-video/components/main/MainSidebar';
import { AdjustmentsSidebar } from '@/features/image-to-video/components/adjustments/AdjustmentsSidebar';
import Tabs from '@/features/image-to-video/components/common/tabs';

import { env } from '@/constants/env';
import VideoPreview from '@/features/image-to-video/components/common/videoPreview';
import HeaderBar from '@/features/image-to-video/components/common/headerBar';
import ChosenImagesStrip from '@/features/image-to-video/components/common/chosenImagesStrip';
import { downloadImageToVideo } from '@/features/image-to-video/api/downloadImageToVideo';
import { IMAGE_TO_VIDEO_MAX_IMAGES } from '@/features/image-to-video/constants/options';

export const ImageToVideoPage = () => {
  const vm = useImageToVideoActions();
  const { t } = useTranslation();
  const setImageToVideoState = useSetImageToVideoState();
  const navigate = useNavigate();
  const router = useRouter();
  const route = getRouteApi('/_authenticated/_sidebar-layout/image-to-video');
  const {
    imageId: imageIdFromParams,
    estateId: estateIdFromParams,
    projectId: projectIdFromParams,
  } = route.useSearch();

  const selectedObject = useAtomValue(selectedObjectAtom);
  const setSelectedEstateId = useSetAtom(selectedEstateIdAtom);
  const setSelectedProjectId = useSetAtom(selectedProjectIdAtom);
  const setSelectedImages = useSetAtom(selectedImagesAtom);
  const setRouteHydrated = useSetAtom(routeParamsHydratedAtom);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we only want to hydrate from route params when they change, not when the view-model callbacks change identity.
  useEffect(() => {
    const estateId = estateIdFromParams ?? null;
    const projectId = estateId ? null : (projectIdFromParams ?? null);

    const image: ImageRef | null = imageIdFromParams
      ? { id: imageIdFromParams, url: `${env.API_URL}/images/${imageIdFromParams}` }
      : null;

    setImageToVideoState((prev) => ({
      ...initialImageToVideoState,
      ui: prev.ui,
    }));

    setSelectedEstateId(estateId);
    setSelectedProjectId(projectId);
    setSelectedImages(image ? [image] : []);

    // Keep VM style in sync with atom-backed object context.
    if (estateId) {
      vm.actions.onEstateChange(estateId);
    } else if (projectId) {
      vm.actions.onProjectChange(projectId);
    } else {
      vm.actions.onEstateChange(null);
    }

    setRouteHydrated(true);
  }, [
    estateIdFromParams,
    projectIdFromParams,
    imageIdFromParams,
    setImageToVideoState,
    setRouteHydrated,
    setSelectedEstateId,
    setSelectedImages,
    setSelectedProjectId,
  ]);

  const estateIdForVideos = selectedObject.estateId;
  const projectIdForVideos = selectedObject.projectId;

  const disableSelectObject = Boolean(estateIdFromParams || projectIdFromParams);

  const handleBackClick = () => {
    router.history.back();
  };
  const handleVideosClick = () => {
    if (estateIdForVideos) {
      navigate({
        to: '/estates/$estateId/media/videos',
        params: { estateId: estateIdForVideos },
      });
      return;
    }

    if (projectIdForVideos) {
      navigate({
        to: '/projects/$projectId/media/videos',
        params: { projectId: projectIdForVideos },
      });
      return;
    }

    toast.error(t('image-to-video-missing-context-for-videos'));
  };
  // For now, we want to hide the tabs and just show the main sidebar. Adjustments will be added in a future iteration.
  const showTabs = false;

  const handleDownloadClick = async () => {
    try {
      await downloadImageToVideo({
        previewUrl: vm.runtime.previewUrl,
        taskId: vm.runtime.lastCompletedTaskId ?? vm.runtime.taskId,
        fileName: vm.runtime.videoName ?? 'image-to-video.mp4',
      });
    } catch (error) {
      toast.error(t('image-to-video-download-error'));
      // biome-ignore lint/suspicious/noConsole: ignored
      console.error('Failed to download image-to-video', error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 flex-col rounded-2xl bg-white shadow-md shadow-black/10">
        <HeaderBar
          videoName={vm.runtime.videoName ?? 'image-to-video.mp4'}
          onBackClick={handleBackClick}
          onVideosClick={handleVideosClick}
          onVideoNameChange={vm.actions.setVideoName}
          onDownload={
            vm.runtime.previewUrl || vm.runtime.lastCompletedTaskId
              ? handleDownloadClick
              : undefined
          }
        />

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside className="flex w-full flex-col md:w-[340px]">
            {showTabs ? (
              <div className="px-6 pt-4">
                <Tabs activeTab={vm.ui.activeTab} onTabChange={vm.actions.setTab} />
              </div>
            ) : null}

            <div className="flex-1 px-6 pb-6">
              {vm.ui.activeTab === 'main' ? (
                <MainSidebar
                  model={vm}
                  initialEstateId={estateIdFromParams ?? null}
                  initialProjectId={projectIdFromParams ?? null}
                  disableSelectObject={disableSelectObject}
                />
              ) : (
                <AdjustmentsSidebar model={vm} />
              )}
            </div>
          </aside>

          <main className="flex flex-1 flex-col bg-white md:rounded-br-2xl">
            <div className="flex bg-white">
              <VideoPreview runtime={vm.runtime} onRetry={vm.actions.onRetry} />
            </div>
            <div className="min-h-[260px] bg-white">
              <div className="flex items-baseline px-6 pb-2 pt-4">
                <div className="text-base font-semibold text-text-primary">
                  {t('chosen-images')}
                </div>
                <div className="ml-3 text-xs font-semibold text-gray-500">
                  {t('max-images', { value: IMAGE_TO_VIDEO_MAX_IMAGES })}
                </div>
              </div>
              <ChosenImagesStrip
                onAddImages={vm.actions.onAddImages}
                images={vm.style.images}
                startImageId={vm.style.startImageId}
                endImageId={vm.style.endImageId}
                estateId={vm.style.estateId}
                projectId={vm.style.projectId}
                onRemove={vm.actions.onRemoveImage}
                onSelectStart={vm.actions.onStartImageChange}
                onSelectEnd={vm.actions.onEndImageChange}
                onReorder={vm.actions.onReorderImages}
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
